/**
 * Before an EXISTING sale is fiscalized again, ask the write master whether it already is.
 *
 * WHY
 * ---
 * Sales History's batch retry and Sale Detail's retry button decide "not fiscalized" from
 * `!sale.fiscalInfo` on whatever the screen was loaded from. For an organization reading Supabase
 * (`shouldUseSupabase()`), that is the Supabase mirror — and the mirror LAGS the write master by
 * construction: the till commits the receipt to the Firestore sale document first, then mirrors it
 * with a best-effort PATCH that can sit in the outbox for hours on an offline till, or be lost
 * outright. In that window a second till reading Supabase shows the sale as unsigned and offers
 * to sign it again. A fiscal receipt is a tax-reporting event on the merchant's register, the
 * operators take no idempotency key, and nothing downstream can collapse a duplicate.
 *
 * So the retry paths read the MASTER — the Firestore sale document, which every write (including
 * the receipt itself: `updateSale` writes Firestore first) goes through — immediately before they
 * sign, and only an explicit "no receipt there" lets them proceed.
 *
 * WHY `source: 'server'`, and why "unknown" REFUSES. A cached read answers with what this device
 * already knew — the very state that can be stale. When the server cannot be reached the retry is
 * not possible anyway (the fiscal operators are online services too), so refusing costs nothing
 * the cashier could have had; guessing "not fiscalized" could cost a duplicate receipt. A missed
 * receipt can be retried later; a duplicate cannot be taken back.
 *
 * ⚠️ When an organization's WRITES move to Supabase (the per-org write cutover, Phase 6), the
 * master moves with them and this must read it there instead — the same note
 * `global/Firebase/deletedEntityLocation.ts` carries for its own master read.
 */

export type MasterFiscalState =
  | { kind: 'unfiscalized' }
  | { kind: 'fiscalized'; fiscalInfo: unknown }
  | { kind: 'unknown'; reason: 'no-document' | 'unreadable' | 'timeout' | 'no-id' }

interface SnapshotLike {
  exists?: unknown
  data?: () => Record<string, unknown> | undefined
}

interface DocRefLike {
  get: (options?: { source?: 'server' | 'cache' | 'default' }) => Promise<SnapshotLike>
}

export interface MasterFiscalStateDeps {
  /** The Firestore sale document reference for `sale`. Throws when no id can be resolved. */
  resolveRef: (sale: unknown) => DocRefLike
  /** Platform-safe existence check (`snapshotExists` in global/Firebase/types). */
  snapshotExists: (snapshot: SnapshotLike | null | undefined) => boolean
  timeoutMs?: number
}

/** Long enough for a slow mobile network, short enough not to strand the retry spinner. */
export const MASTER_READ_TIMEOUT_MS = 10_000

const TIMED_OUT = Symbol('fiscal-master-read-timeout')

const defaultDeps = (): MasterFiscalStateDeps => {
  // Lazily required: both reach the Jotai atoms and, through them, the screens that import this
  // module — the same cycle `global/Firebase/deletedEntityLocation.ts` documents.
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const refs = require('../global/Firebase/firestoreRefUtils') as {
    getFirestoreRef: (item: unknown, collectionPath: string) => DocRefLike
  }
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const types = require('../global/Firebase/types') as {
    snapshotExists: MasterFiscalStateDeps['snapshotExists']
  }
  return {
    resolveRef: (sale) => refs.getFirestoreRef(sale, 'sales'),
    snapshotExists: types.snapshotExists,
  }
}

/**
 * A receipt, as every reader in the app decides it: any truthy `fiscalInfo`. `{}` counts — it is
 * what the pre-fix backend left on receipts it emptied, and it means "signed", never "unsigned".
 */
const isReceipt = (value: unknown): boolean => Boolean(value)

export async function readMasterFiscalState(
  sale: unknown,
  deps: MasterFiscalStateDeps = defaultDeps(),
): Promise<MasterFiscalState> {
  let ref: DocRefLike
  try {
    ref = deps.resolveRef(sale)
  } catch {
    return { kind: 'unknown', reason: 'no-id' }
  }
  let timer: ReturnType<typeof setTimeout> | undefined
  let snapshot: SnapshotLike
  try {
    const answer = await Promise.race([
      ref.get({ source: 'server' }),
      new Promise<typeof TIMED_OUT>((resolve) => {
        timer = setTimeout(() => resolve(TIMED_OUT), deps.timeoutMs ?? MASTER_READ_TIMEOUT_MS)
      }),
    ])
    if (answer === TIMED_OUT) return { kind: 'unknown', reason: 'timeout' }
    snapshot = answer
  } catch {
    // Offline, permission-denied, an SDK that cannot serve a server read: the question was not
    // answered. Never "unfiscalized" — that is the answer that signs a second receipt.
    return { kind: 'unknown', reason: 'unreadable' }
  } finally {
    clearTimeout(timer)
  }
  // No master document: a Supabase-only row, or an id that names nothing. There is no master to
  // ask, and `updateSale` could not record a new receipt anyway (it updates that document first),
  // so signing one would leave it recorded nowhere.
  if (!deps.snapshotExists(snapshot)) return { kind: 'unknown', reason: 'no-document' }
  const fiscalInfo = typeof snapshot.data === 'function' ? snapshot.data()?.fiscalInfo : undefined
  return isReceipt(fiscalInfo) ? { kind: 'fiscalized', fiscalInfo } : { kind: 'unfiscalized' }
}
